import { faKeyboard, faMouse, faSliders, faTimelineArrow } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dialog,
  DialogCustomContent,
  DialogTitle,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@playcastdotio/Shadcn';
import { Description } from '@radix-ui/react-dialog';
import { useCallback, useContext, useEffect, useState } from 'react';
import pcLogo from '../../../assets/playcast_logo-96x96.png';
import { faGamepadAlt, faGaugeSimpleMax, faXmark } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { PlaycastContext } from '../PlaycastWrapper';
import { Select } from './Select';

export const PlayerInputOptions = () => {
  const context = useContext(PlaycastContext);
  const [open, setOpen] = useState(false);
  const [isGamepadAssignedAutomatically, setIsGamepadAssignedAutomatically] = useState(context.stateStores.gamepadState.autoAssign);
  const [shareGamepadStates, setShareGamepadStates] = useState([
    context.stateStores.gamepadState.shareGamepadOne,
    context.stateStores.gamepadState.shareGamepadTwo,
    context.stateStores.gamepadState.shareGamepadThree,
    context.stateStores.gamepadState.shareGamepadFour,
  ]);

  const onOpenChange = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleGamepadAssignAutomaticallyToggle = () => {
    setIsGamepadAssignedAutomatically((prev) => !prev);
  };

  // const handleShareGamepadToggle = useCallback(
  //   (index: number) => {
  //     setShareGamepadStates((prev) => {
  //       const newState = [...prev];
  //       newState[index] = !newState[index];
  //       return newState;
  //     });
  //   },
  //   [setShareGamepadStates],
  // );

  const handleOptionsApply = () => {
    // Apply options
    context.targetFunctions.setAssignGamepadsAutomatically(isGamepadAssignedAutomatically);
    shareGamepadStates.forEach((state, i) => {
      return context.targetFunctions.setGamepadSharing(i, state);
    });

    setOpen(false);
  };

  const [filterGamepadButtons, setFilterGamepadButtons] = useState(context.stateStores.hostRuleState.enableGamepadFiltering);

  useEffect(() => {
    setFilterGamepadButtons(context.stateStores.hostRuleState.enableGamepadFiltering);
  }, [context.stateStores.hostRuleState.enableGamepadFiltering]);

  // const handleFilterGamepadButtons = () => {
  //   context.targetFunctions.setFilterGamepadButtons(!filterGamepadButtons);
  // };

  const [maxAssignments, setMaxAssignments] = useState(context.stateStores.gamepadState.maxAssignments);

  useEffect(() => {
    setMaxAssignments(context.stateStores.gamepadState.maxAssignments);
  }, [context.stateStores.gamepadState.maxAssignments]);

  const handleMaxAssignmentsChange = (value: number) => {
    context.targetFunctions.setMaxGamepadAssignments(value);
  };

  const handleGlobalKeyboardToggle = () => {
    const currentGlobalKeyboardState = context.stateStores.globalInputState.keyboard;
    return context.targetFunctions.setAllowedKeyboardInput(!currentGlobalKeyboardState);
  };

  const handleGlobalMouseToggle = () => {
    const currentGlobalMouseState = context.stateStores.globalInputState.mouse;
    return context.targetFunctions.setAllowedMouseInput(!currentGlobalMouseState);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <div>
        <Button
          onClick={() => setOpen(true)}
          className='bg-primary theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] hover:bg-primary/60 focus:bg-midnight active:bg-primary relative m-0 size-full px-2 text-white'
        >
          <FontAwesomeIcon icon={faGamepadAlt} className='text-white' />
          <span className='pl-2'>Default Inputs</span>
        </Button>
      </div>
      <DialogCustomContent className='theme-gray:bg-dark max-w-lg text-white sm:min-w-[425px]'>
        <div className='bg-primary z-50 flex w-full items-center rounded-t-lg shadow-sm'>
          <div className='ml-4 flex shrink-0 items-center gap-x-2'>
            <img src={pcLogo} alt='Logo' className='h-7 w-7' />
            <h1 className='text-md font-headline font-semibold text-white'>Default Input Settings</h1>
          </div>
          <div className='flex-grow' />
          <Button
            size='icon'
            variant='ghost'
            className='h-10 w-9 rounded-t-lg text-gray-300 hover:bg-inherit hover:text-white'
            onClick={onOpenChange}
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </div>
        <div className='flex flex-col gap-10 p-6 pb-0'>
          <div className='flex flex-col gap-6'>
            <div className='border-primary rounded-md border-2 p-2'>
              <div className='flex gap-4'>
                <div className='flex items-center justify-center text-center'>
                  <FontAwesomeIcon
                    icon={faTimelineArrow}
                    className={clsx(isGamepadAssignedAutomatically ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                  />
                </div>
                <Label htmlFor='share-gamepad-auto' className='text-md flex flex-1 cursor-pointer font-light'>
                  Assign Gamepad Automatically
                </Label>

                <Switch
                  id='share-gamepad-auto'
                  checked={isGamepadAssignedAutomatically}
                  onCheckedChange={handleGamepadAssignAutomaticallyToggle}
                  className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
              </div>
              {isGamepadAssignedAutomatically && (
                <div className='theme-midnight:bg-gray-800 bg-darker flex rounded-b-lg p-2'>
                  {/* dropdown for maxAssignments from 0-4 */}
                  <div className='flex items-center justify-center text-center'>
                    <FontAwesomeIcon icon={faGaugeSimpleMax} className='h-6 w-6 text-emerald-400' />
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild id='max-assignments-tooltip-trigger'>
                          <Label htmlFor='max-assignments' className='text-md ml-4 flex flex-1 cursor-pointer font-light'>
                            Max Gamepads
                          </Label>
                        </TooltipTrigger>
                        <TooltipContent id='max-assignments-tooltip-content'>
                          <p>When enabled, new players are automatically assigned the next available controller slot.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select
                    selected={maxAssignments.toString()}
                    onChange={(e) => handleMaxAssignmentsChange(parseInt(e))}
                    className='ml-auto w-auto rounded-md bg-gray-700 p-2 text-white shadow-xl'
                    data={[
                      { value: '0', label: '0' },
                      { value: '1', label: '1' },
                      { value: '2', label: '2' },
                      { value: '3', label: '3' },
                      { value: '4', label: '4' },
                    ]}
                  />
                </div>
              )}
            </div>

            <div className='border-primary flex gap-4 rounded-md border-2 p-2'>
              <div className='flex items-center justify-center text-center'>
                <FontAwesomeIcon
                  icon={faKeyboard}
                  className={clsx(context.stateStores.globalInputState.keyboard ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                />
              </div>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild id='global-keyboard-tooltip-trigger'>
                    <Label htmlFor='global-keyboard-toggle' className='text-md flex flex-1 cursor-pointer font-light'>
                      Share Keyboard by Default
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent id='global-keyboard-tooltip-content'>
                    <p>When enabled, new players will be allowed to use your keyboard.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Switch
                id='global-keyboard-toggle'
                checked={context.stateStores.globalInputState.keyboard}
                onCheckedChange={handleGlobalKeyboardToggle}
                className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
            </div>

            <div className='border-primary flex gap-4 rounded-md border-2 p-2'>
              <div className='flex items-center justify-center text-center'>
                <FontAwesomeIcon
                  icon={faMouse}
                  className={clsx(context.stateStores.globalInputState.mouse ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                />
              </div>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild id='global-mouse-tooltip-trigger'>
                    <Label htmlFor='global-mouse-toggle' className='text-md flex flex-1 cursor-pointer font-light'>
                      Share Mouse by Default
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent id='global-mouse-tooltip-content'>
                    <p>When enabled, new players will be allowed to use your mouse.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Switch
                id='global-mouse-toggle'
                checked={context.stateStores.globalInputState.mouse}
                onCheckedChange={handleGlobalMouseToggle}
                className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
            </div>
          </div>

          {/* <div className='flex flex-col gap-6'>
            {shareGamepadStates.map((_, i) => (
              <div key={i + '-share-gamepads'} className='flex gap-4'>
                <div className='flex items-center justify-center text-center'>
                  <FontAwesomeIcon
                    icon={faGamepadAlt}
                    className={clsx(shareGamepadStates[i] ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                  />
                </div>
                <Label htmlFor={`share-gamepad-${i + 1}`} className='text-md flex flex-1 cursor-pointer font-light'>
                  Share Gamepad {i + 1}
                </Label>
                <Switch
                  id={`share-gamepad-${i + 1}`}
                  checked={shareGamepadStates[i]}
                  onCheckedChange={() => handleShareGamepadToggle(i)}
                  className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
              </div>
            ))}
          </div>
          <div className='flex flex-col gap-6'>
            <div className='flex gap-4'>
              <div className='flex items-center justify-center text-center'>
                <FontAwesomeIcon
                  icon={faGamepadAlt}
                  className={clsx(shareGamepadStates[4] ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                />
              </div>
              <Label htmlFor={`share-gamepad-5`} className='text-md flex flex-1 cursor-pointer font-light'>
                Filter Start and Select Buttons
              </Label>
              <Switch
                id={`share-gamepad-5`}
                checked={filterGamepadButtons}
                onCheckedChange={() => handleFilterGamepadButtons()}
                className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
            </div>
          </div> */}
        </div>
        <div className='flex flex-1 justify-end p-4'>
          <Button onClick={handleOptionsApply} className='m-0 w-fit bg-amber-400 px-8 hover:opacity-90'>
            Apply
          </Button>
        </div>
      </DialogCustomContent>
    </Dialog>
  );
};
